import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NavigationExtras, Router } from '@angular/router';
import { MsalService} from '@azure/msal-angular'; // MSAL
import { AuthenticationResult } from '@azure/msal-browser';
import jwt_decode from 'jwt-decode';
import { AppState } from './store/app.state';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { environment } from 'src/environments/environment';
import { msalConfig } from './msalConfig';
import { CommonService } from './services/common.service';
import { PrepaLookupDataResourceService } from './services/gen';
import { LoadingService } from './services/loading/loading.service';
import { SharedCodeService } from './services/shared-code.service';
import { CommonUtilResourceService } from './services/HearingWebService';
import {
	EmlAdminRoleCode,
	EnvMaps,
	FepaRegistrationApproverRoleCode,
	FepaRegistrationManagerRoleCode,
	FoiaAdminRoleCode,
	GlobalUserRoleCode,
	HEARING_ALLOWED_OFFICES,
	LookUpDataTypes,
	OFPAdminRoleCode,
	RegistrationApproverRoleCode,
	RegistrationManagerRoleCode,
	SltpAdminRoleCode,
	SystemicAdminRoleCode,
} from './shared/common-structures/app.constants';
import { CommonParams, OauthToken } from './shared/common-structures/common-params';
import { OfficeRole } from './shared/common-structures/office-role';
import { TokenUserInfo } from './shared/common-structures/token-user-info';
import { AlertService } from './shared/ims-alerts/ims-alerts.service';
import { ServiceErrorDialogComponent } from './shared/service-error-dialog/service-error-dialog.component';
import { initializeDatadogRum } from './shared/utility/initializeDatadogRum';
import { Utils } from './shared/utility/utils';
import { Store} from '@ngrx/store';
import { getLoading } from 'src/app/store/shared/shared.selector';
@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
	envMaps: any = EnvMaps;
	commonParams: CommonParams;
	usingKeyBoard: boolean;
	fepaComponents: string[] = ['documents', 'reports', 'fepadualfile'];
	enableNavBar: boolean;
	username: string;
	loginForm: UntypedFormGroup;
	showTestLogin = false;

	isIframe = false;
	msalToken: string;
	msalIdToken: string;
	azureUsername: string;
	showLoading: Observable<boolean>;
	showOptions: boolean = true;
	isScrolled = false;
	environment = environment;
	hasLoggedOut: boolean = false;

	public breadcrumbsList$: Observable<any>;
	breadcrumbsList = [];
	private readonly _destroying$ = new Subject<void>();

	// if user uses keyboard, the current focused item gets a cyan border to improve visual focus.
	@HostListener('mousedown') onMouseDown() {
		this.usingKeyBoard = false;
		this.cmnServ.setKeyboardInput(false);
	}

	/**
	 * @description accessibility skip to main content.
	 */
	@HostListener('keydown.alt.m') onAltM() {
		const curRoute = this.router.url;
		this.usingKeyBoard = true;
		this.cmnServ.setKeyboardInput(true);
		if (curRoute.includes('/dashboard') || curRoute.includes('/systemic-investigation')) {
			let ele: any = document.querySelector('.m-focus');
			if (ele != null) {
				ele.focus();
			}
			//  else {
			// 	const rawHtmlCollection = document.getElementsByTagName('a');
			// 	const caseLinks = Array.from(rawHtmlCollection).filter((i) => i.href.includes('case-details/'));
			// 	if (caseLinks && caseLinks.length > 0) {
			// 	 caseLinks[0].focus();
			// 	}
			// }
		}
		// more can be added
	}

	@HostListener('keydown', ['$event']) onKeyDown(event) {
		// 9 is the code for tab
		if (event.code === 'Tab' || event.keyCode === 9) {
			this.usingKeyBoard = true;
			this.cmnServ.setKeyboardInput(true);
		}

		// shortcut for developer for test purpose
		if (event.code === 'ControlRight' && this.showOptions) {
			this.loginForm.get('username').setValue('fepatestuser');
			this.handleExternalUsers();
		}
	}

	@HostListener('window:scroll', ['$event'])
	onScroll(_event) {
		if (window.scrollY > 80) {
			document.body.classList.add('fixed');
		} else {
			document.body.classList.remove('fixed');
		}

		// if user scrolls below header, the sticky menu header set to be appear.
		if (window.pageYOffset >= 50) {
			this.isScrolled = true;
			this.cmnServ.setStickyMenuHeader(true);
		} else {
			this.isScrolled = false;
			this.cmnServ.setStickyMenuHeader(false);
		}
	}

	/**
	 * @description injects all the dependencies and captures router events to get staff id
	 * @params dependencies
	 */
	constructor(
		public cmnServ: CommonService,
		private router: Router,
		private alert: AlertService,
		private loader: LoadingService,
		private formBuilder: UntypedFormBuilder,
		private msalService: MsalService,
		private store: Store<AppState>,
		private sharedCodeServ: SharedCodeService,
		private dialog: MatDialog,
		private lookUpServ: PrepaLookupDataResourceService,
		private commonUtilService: CommonUtilResourceService
	) {
		initializeDatadogRum();
		this.breadcrumbsList$ = this.cmnServ.getBreadCrumbsList();
		this.cmnServ.onLogout.subscribe((res) => {
			if (res != null) {
				this.showOptions = res;
				this.dialog.closeAll();

				// Set logout flag to prevent auto-authentication
				this.hasLoggedOut = true;
				sessionStorage.setItem('hasLoggedOut', 'true');

				// Ensure test login options are available in lower environments after logout
				this.checkEnvironment();

				try {
					if (this.msalService && this.msalService.instance) {
						this.msalService.instance.setActiveAccount(null);

						try {
							this.msalService.instance.clearCache();
						} catch (clearError) {
							console.warn('Could not clear MSAL cache:', clearError);
						}
					}
				} catch (error) {
					console.error('Error clearing MSAL accounts:', error);
				}

				this.msalToken = null;
				this.msalIdToken = null;
				this.azureUsername = null;
				this.username = null;
			}
		});
		this.cmnServ.onServiceError.subscribe((res) => {
			if (res) {
				this.showServiceError();
			}
		});
	}

	showServiceError() {
		this.dialog.open(ServiceErrorDialogComponent, { width: '450px', height: '300px' });
	}

	/**
	 * @note only show loader while login. loader should be closed in target components.
	 */
	async ngOnInit(): Promise<any> {
		try{
			await this.msalService.instance.initialize();

			if (this.isLoggedOut()) {
				this.msalService.instance.setActiveAccount(null);
				try {
					this.msalService.instance.clearCache();
				} catch (clearError) {
					console.warn('Could not clear MSAL cache on init:', clearError);
				}
				this.showOptions = true;
				this.checkEnvironment();
				this.initGuestLoginForm();
			}
			this.store.select(getLoading).subscribe((setLoading) => {
				if (setLoading) this.loader.show();
				else this.loader.hide();
			});
			this.isIframe = window.location !== window.parent.location;
			this.checkEnvironment();
			if (this.isIframe) {
				this.loader.show();
				this.handleIIGUsers();
			}
			this.initGuestLoginForm();

			// For automated test script purposes, grab the username from the URL param and log in
			if (
				environment.name === 'LOCAL' ||
				environment.name === 'DEV' ||
				environment.name === 'TEST' ||
				environment.name === 'TESTLIT' ||
				environment.name === 'UAT' ||
				environment.name === 'TRAIN'
			) {
				const testUsername = Utils.getParameterByName('username', window.location.search);

				if (!Utils.isNullCondition(testUsername) && !this.isLoggedOut()) {
					this.getPasswordGrantLoginInfo(testUsername, environment.testuserPassword);
					return;
				}
			}
			const sessionUrl = sessionStorage.getItem("prevPageVisitData");
			const sessionUrlObject = sessionUrl ?  JSON.parse(sessionUrl) : null;

			if (sessionUrlObject &&
			    !environment.redirectUri.includes(sessionUrlObject.pageUrl) &&
			    this.msalService.instance.getAllAccounts().length > 0 &&
			    !this.isLoggedOut()) {
				this.msalService.instance.setActiveAccount(this.msalService.instance.getAllAccounts()[0]);
				this.getMSALToken();
				return;
			} else if (this.isLoggedOut()) {
				this.showOptions = true;
				this.checkEnvironment();
				return;
			}
			this.msalService.instance.handleRedirectPromise().then((authResult: AuthenticationResult)=>{
				// Skip all MSAL processing if user is in logged out state
				if (this.isLoggedOut()) {
					this.showOptions = true;
					this.checkEnvironment();
					return;
				}

				// Check if user signed in
				const account = authResult && authResult.account;
				if(account){
					this.clearLogoutState();
					this.msalService.instance.setActiveAccount(account);
					this.azureUsername = account['userName'];
					this.setMsalAuthTokens(authResult);
					this.sendMSALToken();
					return;
				} else {
					const accounts = this.msalService.instance.getAllAccounts();
					if (accounts.length > 0 && !this.isLoggedOut()) {
						this.msalService.instance.setActiveAccount(accounts[0]);
						this.getMSALToken();
					} else {
						this.showOptions = true;
						this.checkEnvironment();
					}
				}
			}).catch((_err) => {
				this.showOptions = true;
				this.checkEnvironment();
			});
		}
		catch (err: any) {
			this.alert.error('An Error occured while processing your request', err);
		}
	}

	private handleIIGUsers = () => {
		const windowUrl = new URL(window.location.href);
		const username = windowUrl.searchParams.get('username');
		const password = windowUrl.searchParams.get('password') || environment.testuserPassword;
		const chargeNumber = windowUrl.searchParams.get('chargenumber');
		if (Utils.isEmptyCondition(username) || Utils.isEmptyCondition(password)) {
			this.errorHandler("User doesn't have valid role.");
		}
		this.getIIGLoginDetails(username, password, chargeNumber);
	};

	loginPopup() {
		this.clearLogoutState();
		this.showOptions = true;
		this.checkEnvironment();

		try {
			this.msalService.loginPopup({
				prompt: 'select_account',
				scopes: msalConfig.consentScopes
			}).pipe(takeUntil(this._destroying$))
			  .subscribe({
				next: (authResult: AuthenticationResult) => {
					const account = authResult && authResult.account;
					if(account){
						this.clearLogoutState();
						this.msalService.instance.setActiveAccount(account);
						this.azureUsername = account['userName'];
						this.setMsalAuthTokens(authResult);
						this.sendMSALToken();
					}
				},
				error: (err: any) => {
					console.log('Login popup failed or was cancelled:', err);
					this.showOptions = true;
					this.checkEnvironment();
				}
			});
		} catch (error) {
			console.error('Error launching login popup:', error);
			this.showOptions = true;
			this.checkEnvironment();
		}
	}

	// Call oAuth
	getIIGLoginDetails(uname: string, pwd: string, chargeNumber?: string) {
		const oAuth$ = this.cmnServ.getOathToken(uname, pwd);
		oAuth$.subscribe(
			(oauthToken) => {
				if (Utils.notNullCondition(oauthToken)) {
					this.cmnServ.setOauthToken(oauthToken);
					this.setCommonParams(oauthToken, chargeNumber);
					// decode user info
					const tokenUser: TokenUserInfo = this.prepareTokenUser(oauthToken.access_token);
					//remove the prefix "ROLE_" from every entry
					const roleAssignments = tokenUser.authorities ? tokenUser.authorities.map((item) => item.slice(5)) : [];
					if (
						!roleAssignments.length ||
						!roleAssignments.find((roleItem) => roleItem.includes('PRIVATE_IIG_SUBMITTER'))
					) {
						this.errorHandler("User doesn't have valid role. Please contact system administrator.");
						return;
					}
					this.cmnServ.setUserDetails(tokenUser);
					this.cmnServ.setRoleAccess(roleAssignments);
					this.queryOfficesRolesList(roleAssignments, false);
					this.queryTimezonesLookup();
				} else {
					this.errorHandler('Invalid user details. Please contact system administrator.');
				}
			},
			(error) => {
				if (error?.error.error === 'invalid_grant') {
					this.errorHandler('Invalid login credentials. Please check your user name.');
				} else {
					this.errorHandler('Error Occurred while getting user details. Please contact system administrator.');
				}
			}
		);
	}

	private getPasswordGrantLoginInfo(uname: string, pwd: string) {
		const oAuth$ = this.cmnServ.getOathToken(uname, pwd);
		oAuth$.subscribe(
			(oauthToken) => {
				this.clearLogoutState();

				if (Utils.notNullCondition(oauthToken)) {
					if (!Utils.notNullCondition(oauthToken?.access_token)) {
						this.errorHandler('Authentication succeeded, but no ARC access token was returned. Please contact system administrator.');
						return;
					}

					const tokenUser: TokenUserInfo = this.prepareTokenUser(oauthToken.access_token);
					const resolvedUserEmail = oauthToken.userEmail || tokenUser?.userEmail || this.azureUsername || '';
					oauthToken.userEmail = resolvedUserEmail;
					oauthToken.userFullName = oauthToken.userFullName || tokenUser?.userFullName || uname;

					const pattern = new RegExp('EEOC.GOV');
					this.cmnServ.userType = pattern.test(resolvedUserEmail.toUpperCase()) ? 'Member' : 'Guest';

					this.cmnServ.setOauthToken(oauthToken);
					this.setCommonParams(oauthToken);
					const roleAssignments = tokenUser.authorities ? tokenUser.authorities.map((item) => item.slice(5)) : [];
					if (roleAssignments.length === 0) {
						this.errorHandler('No active roles found. Please contact system administrator.');
						return;
					}
					this.cmnServ.setUserDetails(tokenUser);
					this.cmnServ.setRoleAccess(roleAssignments);
					this.queryOfficesRolesList(roleAssignments);
					this.queryTimezonesLookup();
				} else {
					this.errorHandler('Invalid user detail ID. Please contact system administrator.');
				}
			},
			(error) => {
				if (error?.error?.error === 'invalid_grant') {
					this.errorHandler('Invalid login credentials. Please check your user name.');
				} else {
					const backendMessage = this.getOauthExchangeErrorMessage(error);
					if (backendMessage) {
						this.alert.error(backendMessage, error);
					} else {
						this.errorHandler('Error Occurred while getting user details. Please contact system administrator.');
					}
				}
			}
		);
	}

	redirectTo(uri: string) {
		this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => this.router.navigate([uri]));
	}

	// Check if User logged in - MSAL
	/*
	getUserAccount() {
		return !!this.msalService.getAccount();
	}
	*/

	// get MSAL Token
	getMSALToken() {
		if (this.isLoggedOut()) {
			this.showOptions = true;
			this.checkEnvironment();
			return;
		}

		this.loader.show();
		const accessTokenRequest = {
			scopes: msalConfig.consentScopes,
			forceRefresh: false
		};
		this.msalService
			.acquireTokenSilent(accessTokenRequest).pipe(takeUntil(this._destroying$))
			.subscribe({
				next: (accessTokenResponse) => {
					this.azureUsername = accessTokenResponse.account['userName'];
					this.setMsalAuthTokens(accessTokenResponse);
					this.sendMSALToken();
				},
				error: () => {
					this.msalService.acquireTokenPopup(accessTokenRequest).pipe(takeUntil(this._destroying$)).subscribe({
						next: (accessToken: any) => {
							this.setMsalAuthTokens(accessToken);
							this.sendMSALToken();
						},
						error: () => {
							this.loader.hide();
							this.clearLogoutState();
							this.showOptions = true;
							this.checkEnvironment();
						}
					});
				}
			});
	}

	// send to Auth endpoint
	sendMSALToken() {
		this.clearLogoutState();
		this.loader.show();
		// The oauth2 service validates the Azure AD access token; the id token is only a fallback.
		const primaryToken = this.msalToken || this.msalIdToken;
		const fallbackToken =
			this.msalIdToken && this.msalIdToken !== primaryToken ? this.msalIdToken : undefined;

		this.getLoginInfo(primaryToken, fallbackToken);
	}

	// Call oAuth
	getLoginInfo(token: string, fallbackToken?: string, hasRetriedWithFallback = false, originalError?: any) {
		const oAuth$ = this.cmnServ.getOathToken(token);
		oAuth$.subscribe(
			(oauthToken) => {
				this.clearLogoutState();

				if (Utils.notNullCondition(oauthToken)) {
					if (!Utils.notNullCondition(oauthToken?.access_token)) {
						this.errorHandler('Authentication succeeded, but no ARC access token was returned. Please contact system administrator.');
						return;
					}

					const tokenUser: TokenUserInfo = this.prepareTokenUser(oauthToken.access_token);
					const resolvedUserEmail = oauthToken.userEmail || tokenUser?.userEmail || this.azureUsername || '';
					oauthToken.userEmail = resolvedUserEmail;
					oauthToken.userFullName = oauthToken.userFullName || tokenUser?.userFullName || this.azureUsername;

					const pattern = new RegExp('EEOC.GOV');
					this.cmnServ.userType = pattern.test(resolvedUserEmail.toUpperCase()) ? 'Member' : 'Guest';

					this.cmnServ.setOauthToken(oauthToken);
					this.setCommonParams(oauthToken);
					//remove the prefix "ROLE_" from every entry
					const roleAssignments = tokenUser.authorities ? tokenUser.authorities.map((item) => item.slice(5)) : [];
					if (roleAssignments.length === 0) {
						this.errorHandler('No active roles found. Please contact system administrator.');
						return;
					}
					this.cmnServ.setUserDetails(tokenUser);
					this.cmnServ.setRoleAccess(roleAssignments);
					this.queryOfficesRolesList(roleAssignments);
					this.queryTimezonesLookup();
				} else {
					this.errorHandler('Invalid user detail ID. Please contact system administrator.');
				}
			},
			(error) => {
				if (this.shouldRetryOauthExchange(error, token, fallbackToken, hasRetriedWithFallback)) {
					this.getLoginInfo(fallbackToken, undefined, true, error);
					return;
				}

				// Report the first attempt's failure; the fallback token is the less likely one to be accepted.
				const reportedError = originalError || error;

				console.error('oauth2 token exchange failed', {
					url: reportedError?.url,
					status: reportedError?.status,
					body: reportedError?.error,
					fallbackStatus: originalError ? error?.status : undefined,
				});

				if (reportedError?.error?.error === 'invalid_grant') {
					this.errorHandler('Invalid login credentials. Please check your user name.');
				} else {
					this.loader.hide();
					const backendMessage = this.getOauthExchangeErrorMessage(reportedError);
					if (backendMessage) {
						this.alert.error(backendMessage, reportedError);
					} else {
						this.cmnServ.showServiceError();
					}
				}
			}
		);
	}

	handleEEOCuser() {
		this.clearLogoutState();
		this.msalService.loginRedirect({
			prompt: msalConfig.prompt,
			scopes: msalConfig.consentScopes,
		});
	}

	private setMsalAuthTokens(authResult: Partial<AuthenticationResult>) {
		this.msalToken = authResult?.accessToken || null;
		this.msalIdToken = authResult?.idToken || null;
	}

	private shouldRetryOauthExchange(
		error: any,
		attemptedToken: string,
		fallbackToken?: string,
		hasRetriedWithFallback = false
	): boolean {
		return (
			!hasRetriedWithFallback &&
			!!fallbackToken &&
			fallbackToken !== attemptedToken &&
			(error?.status === 401 || error?.status === 403 || error?.error?.error === 'invalid_grant')
		);
	}

	private getOauthExchangeErrorMessage(error: any): string | null {
		// error.error can be a parse failure ({ error: SyntaxError, text }) when the route returns HTML.
		const message = [
			error?.error?.error_description,
			error?.error?.message,
			error?.error?.error,
			error?.message,
		].find((value) => typeof value === 'string' && value.trim());

		if (message) {
			return message;
		}

		return null;
	}

	/**
	 * @description decode token user and add some fields
	 */
	private prepareTokenUser(token: string): TokenUserInfo {
		const tokenUser: TokenUserInfo = jwt_decode(token);
		const fallbackFullName = tokenUser.userFullName || tokenUser.user_name || tokenUser.userEmail || this.azureUsername || '';
		tokenUser.userFullName = fallbackFullName;
		const nameArray = fallbackFullName.trim().split(/\s+/).filter(Boolean);
		tokenUser.userFirstName = nameArray[0] || '';
		tokenUser.userLastName = nameArray.length > 1 ? nameArray.slice(1).join(' ') : '';
		return tokenUser;
	}

	/**
	 * @description set common params to behavior subject
	 */
	private setCommonParams(oauthToken: OauthToken, chargeNumber?: string) {
		const currentRoute = window.location.search;
		this.commonParams = {
			userDetailId: Utils.getParameterByName('userdetailid', currentRoute) || oauthToken.userId,
			chargeNumber:
				Utils.getParameterByName('chargenumber', currentRoute) ||
				Utils.getParameterByName('casenumber', currentRoute) ||
				chargeNumber,
			readOnly: Utils.getParameterByName('readonly', currentRoute),
			context: Utils.getParameterByName('context', currentRoute),
		};
		this.cmnServ.setCommonParams(this.commonParams);
	}

	/**
	 * @description check if logged in user has registration roles
	 * @param roleAssignments
	 */
	private hasOnlyRegistrationRole(roleAssignments: string[]): boolean {
		const hasRegistrationsRole = roleAssignments?.some((x) =>
			[
				GlobalUserRoleCode,
				RegistrationApproverRoleCode,
				RegistrationManagerRoleCode,
				FepaRegistrationApproverRoleCode,
				FepaRegistrationManagerRoleCode,
			].some((r) => x.indexOf(r) >= 0)
		);
		const nonRegistrationRoles = roleAssignments?.filter(
			(element) =>
				!element.includes(GlobalUserRoleCode) &&
				!element.includes(RegistrationApproverRoleCode) &&
				!element.includes(RegistrationManagerRoleCode) &&
				!element.includes(FepaRegistrationApproverRoleCode) &&
				!element.includes(FepaRegistrationManagerRoleCode)
		);
		return !nonRegistrationRoles?.length && hasRegistrationsRole;
	}

	private isSystemicAdminRole(roleAssignments: string[]): boolean {
		return roleAssignments.some((x) => x.includes(SystemicAdminRoleCode));
	}

	private isFoiaAdmin(roleAssignments: string[]): boolean {
		return roleAssignments.some((x) => x.includes(FoiaAdminRoleCode));
	}

	private isEmlAdmin(roleAssignments: string[]): boolean {
		return roleAssignments.some((x) => x.includes(EmlAdminRoleCode));
	}

	/**
	 * @description Actions to take when error happens
	 */
	private errorHandler(msg: string) {
		this.loader.hide();
		this.alert.error(msg);
	}

	private checkEnvironment() {
		const envName = environment.name;
		if (envName === 'LOCAL' || envName === 'DEV' || envName === 'TEST' || envName === 'TESTLIT' || envName === 'UAT' || envName === 'TRAIN') {
			this.showTestLogin = true;
		}
	}

	private initGuestLoginForm() {
		this.loginForm = this.formBuilder.group({
			username: new UntypedFormControl(null),
		});
	}

	// External Users
	handleExternalUsers() {
		const username = this.loginForm.get('username').value;
		if (Utils.isEmptyCondition(username)) {
			this.alert.warn('Please enter user name.');
		} else {
			this.clearLogoutState();
			this.loader.show();
			this.getPasswordGrantLoginInfo(username, environment.testuserPassword);
		}
	}

	private queryOfficesRolesList(loggedInUserRoleAssignments: string[], regularNavigation = true) {
		const eeocOfficesRequest = this.sharedCodeServ.getSharedCodes(LookUpDataTypes.OFFICE, null, 'PRIVATE');
		const fepaOfficesRequest = this.sharedCodeServ.getSharedCodes(LookUpDataTypes.OFFICE, null, 'FEPA', true );
		const rolesRequest = this.sharedCodeServ.getRoles();

		// Check if user has hearing office roles
		const hasHearingRoles = loggedInUserRoleAssignments?.some((authority) => {
			const parts = authority.split(':');
			if (parts.length >= 2) {
				const roleCode = parts[0] || '';
				const officeCode = parts[1];
				return roleCode.startsWith('HEARING_') && HEARING_ALLOWED_OFFICES.includes(officeCode);
			}
			return false;
		});

		this.cmnServ._setPrivateOffices([]);
		this.cmnServ._setFepaOffices([]);
		this.cmnServ._setFepaOfficesIncludeObsolete([]);
		this.cmnServ._setRoles([]);
		this.cmnServ._setHearingOffices([]);
		this.cmnServ.setOfficeRoleArray([]);

		// Conditionally add hearing offices request if user has hearing roles
		const requests: any = { eeocOfficesRequest, fepaOfficesRequest, rolesRequest };
		if (hasHearingRoles) {
			requests.hearingOfficesRequest = this.commonUtilService.getHearingOfficeList().pipe(
				catchError(() => of([]))
			);
		}

		forkJoin(requests).subscribe(
			(response: any) => {
				this.loader.hide();
				this.cmnServ._setPrivateOffices(response?.eeocOfficesRequest?.sort(Utils.sortSharedCodes));
				this.cmnServ._setFepaOfficesIncludeObsolete(response?.fepaOfficesRequest?.sort(Utils.sortSharedCodes));
				this.cmnServ._setFepaOffices(response?.fepaOfficesRequest?.filter(
					(fepaOffice) => fepaOffice.obsolete === false).sort(Utils.sortSharedCodes));
				this.cmnServ._setRoles(response?.rolesRequest?.sort(Utils.sortRoles));

				// Set hearing offices if available
				if (hasHearingRoles && response?.hearingOfficesRequest) {
					this.cmnServ._setHearingOffices(response.hearingOfficesRequest);
				}

				if (
					response?.eeocOfficesRequest?.length &&
					response.fepaOfficesRequest?.length &&
					response.rolesRequest?.length
				)
					this.setUserOfficeRolesList(loggedInUserRoleAssignments);

				if (regularNavigation) {
					// navigate user
					let navigationUrl = this.hasOnlyRegistrationRole(loggedInUserRoleAssignments)
						? '/user-management'
						: this.isSystemicAdminRole(loggedInUserRoleAssignments)
						? '/systemic-investigation'
						: this.isEmlAdmin(loggedInUserRoleAssignments)
						? '/employer-representatives'
						: this.cmnServ.hasOnlySearchRedirectRoles()
						? '/search'
						: '/dashboard';
					const fromUrl = this.cmnServ.fromUrl;
					if (fromUrl && fromUrl !== 'login' && fromUrl !== 'error') {
						// remove query parameters
						navigationUrl = fromUrl.split('?')[0];
						this.cmnServ.fromUrl = null;
					}
					this.enableNavBar = this.fepaComponents.includes(navigationUrl) ? false : true;
					const navigationExtras: NavigationExtras = {
						queryParams: { ...this.commonParams },
					};
					// Redirect the user
					this.showOptions = false;
					if (
						navigationUrl !== '/dashboard' ||
						(navigationUrl === '/dashboard' && !window.location.href.includes('/case-details/'))
					) {
						this.router.navigate([navigationUrl], navigationUrl !== '/error' ? navigationExtras : {});
					}
				} else {
					// navigate user
					let navigationUrl = '/iig-new-inquiry';
					const navigationExtras: NavigationExtras = {
						queryParams: { ...this.commonParams },
					};
					// Redirect the user
					this.showOptions = false;
					this.router.navigate([navigationUrl], navigationUrl !== '/error' ? navigationExtras : {});
				}
			},
			(error) => {
				this.loader.hide();
				if (error.status === 422) this.alert.error(error?.error, error);
				else
					this.alert.error(
						'Error happened while processing offices and roles service requests. Please contact system administrator.' , error
					);
			}
		);
	}

	/**
	 * @description Generate the dropdown list for office role selection
	 * @param loggedInUserRoleAssignments
	 */
	private setUserOfficeRolesList(loggedInUserRoleAssignments: string[]) {
		const officeDict = [...(this.cmnServ._getAllOffices() || [])];
		const roleDict = [...(this.cmnServ._getRoles() || [])];
		const hearingOfficeDisplayByCode: { [key: string]: string } = {
			'837': 'East Division',
			'838': 'West Division',
			'839': 'Triage Division',
		};
		const mapHearingOfficeLabel = (label: string): string => {
			if (!label) {
				return '';
			}

			const officeCodeMatch = label.match(/\b(83[789])\b/);
			if (officeCodeMatch?.[1] && hearingOfficeDisplayByCode[officeCodeMatch[1]]) {
				return hearingOfficeDisplayByCode[officeCodeMatch[1]];
			}

			return label;
		};
		const normalizeOfficeLabel = (label: string, isHearingRole: boolean): string => {
			if (!label) {
				return '';
			}

			if (!isHearingRole) {
				return label;
			}

			const mappedLabel = mapHearingOfficeLabel(label);
			return mappedLabel.replace(/\s+Offices?\b/gi, '').replace(/\s{2,}/g, ' ').trim();
		};

		const buildOfficeRoleDescription = (officeLabel: string, roleDisplayText: string, isHearingRole: boolean): string => {
			const normalizedOffice = normalizeOfficeLabel(officeLabel, isHearingRole);
			return `${normalizedOffice} - ${roleDisplayText || ''}`.trim();
		};
		let officeRoles: OfficeRole[];
		let adminRoles = loggedInUserRoleAssignments?.filter(
			(el) => el.includes(GlobalUserRoleCode) || el.includes(OFPAdminRoleCode) || el.includes(SltpAdminRoleCode)
		);
		let updatedLoggedInUserRoleAssignments = loggedInUserRoleAssignments?.filter(
			(el) => !el.includes(GlobalUserRoleCode) && !el.includes(OFPAdminRoleCode) && !el.includes(SltpAdminRoleCode)
		);

		if (adminRoles?.length) {
			// add all offices for global users
			adminRoles?.forEach((el) => {
				const splitedPair = el.split(':');
				const _role = roleDict.find((item) => item.roleCode === splitedPair[0]);
				const isHearingRole = (_role?.roleCode || '').includes('HEARING');
				officeRoles = [
					...(officeRoles || []),
					...(officeDict.map((_office) => {
						const officeRole: OfficeRole = {
							office: _office,
							role: _role,
							description: buildOfficeRoleDescription(_office?.description, _role?.roleDisplayText, isHearingRole),
						};
						return officeRole;
					}) || []),
				];
			});
		}
		if (updatedLoggedInUserRoleAssignments?.length) {
			// normal roles
			// input: `{roleCode}:{officeCode}:{unit}`, i.e. 'ROLE_PRIVATE_STAFF:430:Enforcement-1'
			// unit might be empty
			officeRoles = [
				...(officeRoles || []),
				...(updatedLoggedInUserRoleAssignments.map((pair) => {
					const splitedPair = pair.split(':');
					// Clone the role template so per-office unit assignment doesn't mutate the shared roleDict entry.
					const _roleTemplate = roleDict.find((item) => item.roleCode === splitedPair[0]);
					const _role = _roleTemplate ? { ..._roleTemplate } : _roleTemplate;
					const _office =
						splitedPair[1] === 'ALL'
							? { code: 'ALL', description: 'All Offices' }
							: Utils.getLookupDataByCode(splitedPair[1], officeDict);
					const isHearingRole = (_role?.roleCode || '').includes('HEARING');
					const officeLabel = splitedPair[1] === 'ALL' ? 'All Offices' : _office?.description;
					if (_role) _role.unit = splitedPair[2];
					const officeRole: OfficeRole = {
						office: _office,
						role: _role,
						description: buildOfficeRoleDescription(officeLabel, _role?.roleDisplayText, isHearingRole),
					};
					return officeRole;
				}) || []),
			];
		}
		officeRoles = officeRoles.filter((x) => x.office);
		officeRoles = officeRoles.sort((a, b) => (a.description > b.description ? 1 : -1));
		this.cmnServ.setOfficeRoleArray(officeRoles);
	}

	private queryTimezonesLookup() {
		this.lookUpServ.getLookupDataByDomain('TMZ').subscribe(
			(response) => this.cmnServ._setTimezonesLookup([...(response || [])]?.sort()),
			(error) => {
				if (error?.status === 422) this.alert.error(error?.error, error);
				else this.alert.error('Error happened while getting time zones. Please contact system administrator.', error);
			}
		);
	}

	public scrollToTop() {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	/**
	 * Check if user has logged out to prevent auto-authentication
	 */
	private isLoggedOut(): boolean {
		const sessionFlag = sessionStorage.getItem('hasLoggedOut') === 'true';
		return this.hasLoggedOut || sessionFlag;
	}

	/**
	 * Clear logout state (called on successful login)
	 */
	private clearLogoutState(): void {
		this.hasLoggedOut = false;
		sessionStorage.removeItem('hasLoggedOut');
	}

	ngOnDestroy(): void {
		this._destroying$.next(null);
		this._destroying$.complete();
	}
}
